import type React from "react";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { User as UserIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CustomDatePicker } from "@/components/common/CustomDatePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type SearchAuthorOption,
  CLEAR_AUTHOR_VALUE,
} from "@/features/study-group-stream/utils/streamSearchFilterOptions";
import { StreamSearchConditionClearButton } from "@/features/study-group-stream/components/StreamSearchClearButton";

const DATE_PICKER_TRIGGER_CLASS =
  "h-9 w-full justify-start border-gray-300 bg-white text-sm text-gray-900 pr-8";
const AUTHOR_SELECT_TRIGGER_CLASS = "h-9 w-full pl-8 pr-8";
const SEARCH_ACTIONS_CONTAINER_CLASS = "flex justify-end gap-2";
const SEARCH_BUTTON_CLASS = "w-16 px-2";
const CLOSE_SEARCH_BUTTON_CLASS = "text-gray-500 hover:text-gray-700";
const SEARCH_FILTER_ROW_CLASS = "grid grid-cols-1 sm:grid-cols-4 gap-2";
const SEARCH_FILTER_WRAPPER_CLASS =
  "flex flex-col gap-2 animate-in fade-in slide-in-from-top-2 duration-200";
const SEARCH_FILTER_FIELD_WRAPPER_CLASS = "relative";
const SEARCH_USER_ICON_CLASS =
  "absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400";

export type SearchFiltersPanelProps = {
  from: string;
  to: string;
  authorId: string;
  hasSearchCriteria: boolean;
  authorOptions: SearchAuthorOption[];
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
  onAuthorChange: (value: string) => void;
  onFromClear: () => void;
  onToClear: () => void;
  onAuthorClear: () => void;
  onAuthorFieldOpen?: () => void | Promise<void>;
  isAuthorOptionsLoading?: boolean;
  onFilterKeyDown?: React.KeyboardEventHandler<HTMLElement>;
  onSearch: () => void;
  onExitSearch: () => void;
  startDateLabel: string;
  endDateLabel: string;
  authorLabel: string;
  allAuthorsLabel: string;
  closeSearchLabel: string;
  searchLabel: string;
  clearSelectionLabel: string;
};

const SearchFiltersPanelRaw: React.FC<SearchFiltersPanelProps> = ({
  from,
  to,
  authorId,
  hasSearchCriteria,
  authorOptions,
  onFromChange,
  onToChange,
  onAuthorChange,
  onFromClear,
  onToClear,
  onAuthorClear,
  onAuthorFieldOpen,
  isAuthorOptionsLoading = false,
  onFilterKeyDown,
  onSearch,
  onExitSearch,
  startDateLabel,
  endDateLabel,
  authorLabel,
  allAuthorsLabel,
  closeSearchLabel,
  searchLabel,
  clearSelectionLabel,
}) => {
  const { t } = useTranslation();
  const hasFrom = Boolean(from);
  const hasTo = Boolean(to);
  const hasAuthor = Boolean(authorId) && authorId !== CLEAR_AUTHOR_VALUE;

  return (
    <div className={SEARCH_FILTER_WRAPPER_CLASS}>
      <div className={SEARCH_FILTER_ROW_CLASS}>
        <div className={SEARCH_FILTER_FIELD_WRAPPER_CLASS}>
          <CustomDatePicker
            value={from || ""}
            onChange={onFromChange}
            triggerOnKeyDown={onFilterKeyDown}
            placeholder={startDateLabel}
            className="w-full"
            triggerClassName={DATE_PICKER_TRIGGER_CLASS}
            side="top"
          />
          <StreamSearchConditionClearButton
            shouldRender={hasFrom}
            onClear={onFromClear}
            ariaLabel={clearSelectionLabel}
          />
        </div>
        <div className={SEARCH_FILTER_FIELD_WRAPPER_CLASS}>
          <CustomDatePicker
            value={to || ""}
            onChange={onToChange}
            triggerOnKeyDown={onFilterKeyDown}
            placeholder={endDateLabel}
            className="w-full"
            triggerClassName={DATE_PICKER_TRIGGER_CLASS}
            side="top"
          />
          <StreamSearchConditionClearButton
            shouldRender={hasTo}
            onClear={onToClear}
            ariaLabel={clearSelectionLabel}
          />
        </div>
        <div className={SEARCH_FILTER_FIELD_WRAPPER_CLASS}>
          <Select
            value={authorId}
            onValueChange={onAuthorChange}
            onOpenChange={(open) => {
              if (open) {
                void onAuthorFieldOpen?.();
              }
            }}
          >
            <SelectTrigger
              className={AUTHOR_SELECT_TRIGGER_CLASS}
              onKeyDown={onFilterKeyDown}
            >
              <SelectValue placeholder={authorLabel} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={CLEAR_AUTHOR_VALUE}>
                {allAuthorsLabel}
              </SelectItem>
              {isAuthorOptionsLoading && (
                <SelectItem value="__loading_author__" disabled>
                  {t("common.loading")}
                </SelectItem>
              )}
              {authorOptions.map((authorOption) => (
                <SelectItem
                  className="h-9 [&>span]:w-full"
                  key={authorOption.id}
                  value={authorOption.id}
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <Avatar className="size-6 shrink-0">
                      <AvatarImage
                        src={authorOption.avatarUrl}
                        alt={authorOption.label}
                      />
                      <AvatarFallback className="bg-blue-100 text-xs text-blue-700">
                        {authorOption.label?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex min-w-0 flex-1">
                      <span className="truncate text-sm font-medium leading-none text-current">
                        {authorOption.label}
                      </span>
                    </div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <UserIcon className={SEARCH_USER_ICON_CLASS} />
          <StreamSearchConditionClearButton
            shouldRender={hasAuthor}
            onClear={onAuthorClear}
            ariaLabel={clearSelectionLabel}
          />
        </div>
        <div className={SEARCH_ACTIONS_CONTAINER_CLASS}>
          <Button
            type="button"
            aria-label={searchLabel}
            onClick={onSearch}
            disabled={!hasSearchCriteria}
            size="sm"
            variant="teal"
            className={SEARCH_BUTTON_CLASS}
          >
            {searchLabel}
          </Button>
          <Button
            type="button"
            aria-label={closeSearchLabel}
            variant="ghost"
            size="sm"
            onClick={onExitSearch}
            className={CLOSE_SEARCH_BUTTON_CLASS}
          >
            {closeSearchLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const SearchFiltersPanel = memo(SearchFiltersPanelRaw);
SearchFiltersPanel.displayName = "SearchFiltersPanel";
